import { Divider, Text, vars } from "@vericus/cadmus-ui";

import { clsx } from "clsx";

import lockIllustration from "@/assets/illustrations/student-with-locked-browser.png";
import feedbackImg from "@/assets/illustrations/students-discussing.png";
import { LoadingPage } from "@/components/loading-page";
import { FeedbackTabId } from "@/features/feedback/components/types";
import {
  FeedbackReleaseType,
  SubmissionType,
  useFeedbackSummaryQuery,
  useWorkGroupQuery,
} from "@/generated/graphql";
import { useAssessmentParams } from "@/routes";

import { FeedbackItem } from "../components/feedback-item";
import { FeedbackResults } from "../components/feedback-results";
import { FeedbackItemContent } from "../utils/feedback-visibility";
import * as styles from "./home-page.css";

/**
 * Page on the aphrodite home route that displays the feedback summary before
 * the student can view the full feedback.
 */
export function FeedbackSummaryPage() {
  const { assessmentId, tenant } = useAssessmentParams();

  const { data, loading } = useFeedbackSummaryQuery({
    variables: {
      assessmentId,
    },
  });

  const workGroupResult = useWorkGroupQuery({
    variables: { assessmentId },
  });
  const assessment = data?.assessment;
  const settings = assessment?.settings;
  const groupWorkOutcome = data?.studentOutcome.groupWorkOutcome;
  const groupRelease = groupWorkOutcome?.workOutcomeRelease;
  const individualWorkOutcome = data?.studentOutcome.individualWorkOutcome;
  const individualRelease = individualWorkOutcome?.workOutcomeRelease;
  const groupDraftRelease =
    data?.studentOutcome.groupWorkOutcome?.draftSubmissionOutcomeRelease;
  const individualDraftRelease =
    data?.studentOutcome.individualWorkOutcome?.draftSubmissionOutcomeRelease;

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <LoadingPage />
      </div>
    );
  }

  if (!settings || !data?.studentOutcome || !workGroupResult.data) return null;

  const groupFeedbackReleased = !!groupRelease;
  const individualFeedbackReleased = !!individualRelease;
  const feedbackReleased = !!groupRelease || !!individualRelease;

  const groupDraftFeedbackReleased = !!groupDraftRelease;
  const individualDraftFeedbackReleased = !!individualDraftRelease;
  const draftFeedbackReleased =
    groupDraftFeedbackReleased || individualDraftFeedbackReleased;

  if (settings.feedbackDateDisabled) {
    return (
      <div className={styles.feedbackOverview}>
        <img src={feedbackImg} height={180} width={180} alt="" />
        <div>
          <Text
            kind="displayThree"
            textAlign="center"
            style={{ fontSize: "2rem" }}
          >
            <span style={{ color: vars.colors.lilac500 }}>Feedback</span> has
            been released via your LMS
          </Text>
          <Text kind="bodyMd" textAlign="center" style={{ maxWidth: 700 }}>
            Your grade has been released through the learning management system.
            Please refer to the LMS for your results. Written feedback was not
            provided for this assessment.
          </Text>
        </div>
      </div>
    );
  }

  // Each work outcome (group/individual) can have different release types
  // Determine which breakdown to use for each based on their individual release types
  const groupScoreBreakdown =
    groupRelease?.releaseType === FeedbackReleaseType.Final
      ? data.studentOutcome.final
      : data.studentOutcome.provisional;

  const individualScoreBreakdown =
    individualRelease?.releaseType === FeedbackReleaseType.Final
      ? data.studentOutcome.final
      : data.studentOutcome.provisional;

  // Check if any of the releases are provisional
  // In order to display the final FeedbackResults view, all releases must be final
  // If any one is provisonal, we show provisional for the FeedbackResults
  const hasAnyProvisionalRelease =
    groupRelease?.releaseType === FeedbackReleaseType.Provisional ||
    individualRelease?.releaseType === FeedbackReleaseType.Provisional;

  const overallScoreBreakdown = hasAnyProvisionalRelease
    ? data.studentOutcome.provisional
    : data.studentOutcome.final;

  if ((!feedbackReleased && !draftFeedbackReleased) || !overallScoreBreakdown) {
    return (
      <div className={styles.feedbackOverview}>
        <img src={lockIllustration} height={180} width={180} alt="" />
        <div>
          <Text
            kind="displayThree"
            textAlign="center"
            style={{ fontSize: "2rem" }}
          >
            <span style={{ color: vars.colors.lilac500 }}>Feedback</span> has
            not been released yet
          </Text>
          <Text kind="bodyMd" textAlign="center" style={{ maxWidth: 700 }}>
            Your grade will be released through Cadmus. <br />
            Written feedback will be provided for this assessment.
          </Text>
        </div>
      </div>
    );
  }

  const feedbackViewLevel = hasAnyProvisionalRelease
    ? data.provisionalFeedbackVisibility.feedbackViewLevel
    : data.finalFeedbackVisibility.feedbackViewLevel;

  return (
    <div className={styles.feedbackMain}>
      <FeedbackResults
        isProvisional={hasAnyProvisionalRelease}
        feedbackViewLevel={feedbackViewLevel}
        finalGrade={overallScoreBreakdown.total?.score}
        maxGrade={settings.maxGrade}
        moderatorModifier={
          (overallScoreBreakdown.total?.moderatorModifierComponents || 0) +
          (overallScoreBreakdown.total?.moderatorModifierStudent || 0)
        }
        penaltyScore={overallScoreBreakdown.total?.penaltyScore}
      />
      <div className={clsx(styles.feedbackOverview, styles.feedbackRight)}>
        {settings.groupAssessmentEnabled && groupWorkOutcome && (
          <>
            <FeedbackItem
              id={FeedbackItemContent.Group}
              title="Group Work"
              feedbackVisibility={groupRelease?.feedbackVisibility ?? null}
              weight={groupScoreBreakdown.group?.weight ?? null}
              maxGrade={groupScoreBreakdown.group?.maxScore ?? null}
              finalScore={groupScoreBreakdown.group?.score ?? null}
              submissionType={
                groupFeedbackReleased
                  ? SubmissionType.Final
                  : groupDraftFeedbackReleased
                    ? SubmissionType.Draft
                    : undefined
              }
              feedbackTab={FeedbackTabId.GroupReport}
              workId={groupWorkOutcome.workId}
              tenant={tenant}
              moderatorModifier={groupScoreBreakdown.group?.moderatorModifier}
              penaltyScore={groupScoreBreakdown.group?.penaltyScore}
              releaseDate={groupRelease?.releasedAt}
            />
          </>
        )}
        {settings.individualReportEnabled && individualWorkOutcome && (
          <>
            <Divider />
            <FeedbackItem
              id={FeedbackItemContent.Individual}
              title="Individual Work"
              feedbackVisibility={individualRelease?.feedbackVisibility ?? null}
              weight={individualScoreBreakdown.individual?.weight ?? null}
              maxGrade={individualScoreBreakdown.individual?.maxScore ?? null}
              finalScore={individualScoreBreakdown.individual?.score ?? null}
              submissionType={
                individualFeedbackReleased
                  ? SubmissionType.Final
                  : individualDraftFeedbackReleased
                    ? SubmissionType.Draft
                    : undefined
              }
              feedbackTab={FeedbackTabId.IndividualReport}
              workId={individualWorkOutcome.workId}
              tenant={tenant}
              moderatorModifier={
                individualScoreBreakdown.individual?.moderatorModifier
              }
              penaltyScore={individualScoreBreakdown.individual?.penaltyScore}
              releaseDate={individualRelease?.releasedAt}
            />
          </>
        )}
        {settings.peerEvaluationEnabled && groupWorkOutcome && (
          <>
            <Divider />
            <FeedbackItem
              id={FeedbackItemContent.Peer}
              title="Peer Evaluation"
              feedbackVisibility={groupRelease?.feedbackVisibility ?? null}
              weight={overallScoreBreakdown.peer?.weight ?? null}
              maxGrade={overallScoreBreakdown.peer?.maxScore ?? null}
              finalScore={overallScoreBreakdown.peer?.score}
              submissionType={
                groupFeedbackReleased ? SubmissionType.Final : undefined
              }
              feedbackTab={FeedbackTabId.PeerEvaluation}
              tenant={tenant}
              releaseDate={groupRelease?.releasedAt}
              workId={groupWorkOutcome.workId}
            />
          </>
        )}
        {settings.selfEvaluationEnabled && groupWorkOutcome && (
          <>
            <Divider />
            <FeedbackItem
              id={FeedbackItemContent.Self}
              title="Self Evaluation"
              feedbackVisibility={groupRelease?.feedbackVisibility ?? null}
              weight={overallScoreBreakdown.self?.weight ?? null}
              maxGrade={overallScoreBreakdown.self?.maxScore ?? null}
              finalScore={overallScoreBreakdown.self?.score}
              submissionType={
                groupFeedbackReleased ? SubmissionType.Final : undefined
              }
              feedbackTab={FeedbackTabId.PeerEvaluation}
              workId={groupWorkOutcome.workId}
              tenant={tenant}
              releaseDate={groupRelease?.releasedAt}
            />
          </>
        )}
      </div>
    </div>
  );
}
